export interface Cliente {
  id: string;
  nomeAzienda: string;
  referenteRiprese?: string;
  telefono: string;
  email?: string;
  reelMensili: number;
  giorniPreferitiRiprese: string[];
  piattaforme: PiattaformaSocial[];
  formatReel: string[];
  pubblicazioneSettimanaleGiorni?: string[];
  // Per ogni giorno, i canali social confermati come pubblicati (es. { "Lunedì": ["Instagram"] }).
  // Usato dal Calendario per il colore del pallino: verde=tutti, arancione=parziale.
  pubblicazioneSettimanaleCanali?: Record<string, string[]>;
  orarioPubblicazione?: string;
  /** true = i testi se li scrive il cliente: per noi non c'è nulla da scrivere */
  testiDalCliente?: boolean;
  /** true = il calendario editoriale prevede anche post grafici (copy + immagine) */
  postGrafici?: boolean;
  /** post grafici da pubblicare ogni settimana (0 se non previsti) */
  postGraficiSettimana?: number;
  /** quanti di quei post a settimana sono caroselli di foto reali (03/11/2026): postGraficiSettimana è il
   *  TOTALE dei post (giorni da post del calendario), i post grafici sono il resto */
  caroselliFotoSettimana?: number;
  /** Giorni fissi del post (Lunedì…), senza rotazione (26/10/2026) */
  giorniPost?: string[];
  /** Alternanza continua reel/post a ogni uscita: 0 parte dal reel, 1 dal post (29/10/2026) */
  alternanzaFase?: number | null;
  /** profili su cui pubblica (es. 142 Restaurant e 142 Bistrot); vuoto = uno solo */
  accountPubblicazione?: string[];
  status: 'active' | 'inactive' | 'suspended' | 'archived';
  createdAt: Date;
  updatedAt: Date;
  // Credenziali social: per ogni piattaforma il nome della pagina/account e la password
  nomeInstagram?: string;
  passwordInstagram?: string;
  nomeTikTok?: string;
  passwordTikTok?: string;
  nomeYouTube?: string;
  passwordYouTube?: string;
  nomeFacebook?: string;
  passwordFacebook?: string;
  nomeLinkedIn?: string;
  passwordLinkedIn?: string;
  linkGoogleDrive?: string;
  linkWebAppLead?: string;
  logoUrl?: string;
  brandFont?: string;
  brandColor?: string;
}

// reel = video (hook/corpo/CTA, riprese, montaggio); post = post grafico
// (solo copy + immagine): salta Riprese ed Editing e va in programmazione
// appena il testo è confermato. Vedi utils/tipoContenuto.ts.
export type TipoContenuto = 'reel' | 'post';
// Formato del post (03/11/2026, Michel): «grafica» = post grafico / carosello in computer
// grafica, «foto» = carosello di foto reali scattate nel locale. Come le sezioni di
// post.bymarke.com (Post grafici / Caroselli & foto). Solo per tipo 'post'.
export type FormatoPost = 'grafica' | 'foto';

export interface Contenuto {
  id: string;
  clienteId: string;
  tipo?: TipoContenuto;
  /** solo per i post: computer grafica o foto reali (vuoto = grafica) */
  formatoPost?: FormatoPost;
  /** profili del cliente a cui è destinato (tutti = entrambi, vuoto = non indicato) */
  accountDestinazione?: string[];
  titolo: string;
  hook?: string;
  body?: string;
  cta?: string;
  copyPost?: string;
  stato: StatoContenuto;
  note?: string;
  linkIspirazione?: string;
  linkMontaggio?: string;
  linkPubblicato?: string;
  dataPubblicazione?: Date;
  videoRegistrato: boolean;
  // Quando il videomaker l'ha segnato come registrato (video_recorded_at):
  // decide in che mese conta tra i "Registrati" (reelGiratiNelMese).
  videoRegistratoIl?: Date;
  // "Montato": segnato dalla pagina Editing (tra Videomaker e Programmazione).
  // Come videoRegistrato vive accanto allo stato e non lo cambia: un contenuto
  // registrato e non ancora montato sta in Editing anche se già programmato,
  // perché si può programmare prima del montaggio come prima della
  // registrazione. Vedi Editing.tsx.
  videoMontato?: boolean;
  // "Pronto da programmare": forzato a mano dal Videomaker per far comparire il
  // contenuto in "da programmare" ANCHE se non ancora registrato (schedulazione
  // in anticipo). Resta comunque nella coda del Videomaker finché non lo si
  // segna registrato. Vedi flusso in Videomaker.tsx / Programmazione.tsx.
  prontoDaProgrammare?: boolean;
  // "Rimosso dalla programmazione": tolto a mano dalla vista "Da programmare"
  // col pulsante Rimuovi. Il contenuto sparisce da lì e resta solo nel
  // Copywriter, senza perdere "registrato" né i conteggi. Torna in coda quando
  // il Videomaker preme Programma o lo segna registrato.
  rimossoDaProgrammazione?: boolean;
  campagnaAdv?: boolean;
  // Uscita mancata: l'archiviazione automatica l'ha trovato programmato con
  // la data passata ma non montato o assente da Instagram, e l'ha rimesso da
  // lavorare. Qui la data in cui doveva uscire e il motivo; «È uscito» lo
  // archivia se invece era uscito davvero. Vedi backend ContentArchiver.
  uscitaMancata?: Date;
  uscitaMancataMotivo?: string;
  advUtilizzato?: boolean;
  clipCaricate?: boolean;
  copertinaUrl?: string;
  createdAt: Date;
  updatedAt: Date;
  clientModifiedAt?: Date;
  /** Incrocio Drive × Social: 'presente' (girato, clip sul Drive) o 'recuperato' (scritto dal parlato delle clip). */
  driveFlag?: 'presente' | 'recuperato' | null;
  driveCartella?: string | null;
  /** 'opus' = la spunta «Montato» l'ha messa edit.bymarke.com (reel montato da Opus, via drive.bymarke.com); null = a mano. */
  montatoDa?: string | null;
  /** Il progetto in edit.bymarke.com (solo per il team). */
  montatoLink?: string | null;
  // pubblica.bymarke.com (23/10/2026): conferma del cliente su WhatsApp e
  // uscita su Metricool, scritte da pubblica. Vedi components/BadgePubblica.tsx.
  confermaCliente?: 'attesa' | 'confermato' | 'modifiche' | null;
  confermaClienteIl?: Date;
  confermaClienteNota?: string;
  uscitaStato?: 'in_coda' | 'in_uscita' | 'uscito' | 'errore' | 'mancato' | null;
  uscitaErrore?: string;
}

export enum StatoContenuto {
  BOZZA = 'bozza',
  TESTO_PRONTO = 'testo_pronto',
  REGISTRATO = 'registrato',
  MONTATO = 'montato',
  PROGRAMMATO = 'programmato',
  PUBBLICATO = 'pubblicato',
  ARCHIVIATO = 'archiviato'
}

export enum PiattaformaSocial {
  INSTAGRAM = 'Instagram',
  TIKTOK = 'TikTok',
  YOUTUBE = 'YouTube',
  FACEBOOK = 'Facebook',
  LINKEDIN = 'LinkedIn'
}

export interface ContentFilters {
  clientId?: string;
  status?: StatoContenuto;
  assignedTo?: string;
  dateFrom?: Date;
  dateTo?: Date;
}

export interface DashboardStats {
  active_clients: number;
  monthly_reels_target: number;
  reels_completed: number;
  reels_published: number;
  completion_percentage: number;
  publication_percentage: number;
  client_progress: Array<{
    client_id: string;
    company_name: string;
    target_reels: number;
    completed_reels: number;
    published_reels: number;
  }>;
}

export interface ClientStats {
  total: number;
  active: number;
  inactive: number;
  by_platform: {
    instagram: number;
    tiktok: number;
    youtube: number;
    facebook: number;
    linkedin: number;
  };
  total_monthly_reels: number;
}

export interface ContentStats {
  total: number;
  by_status: {
    draft: number;
    text_ready: number;
    recorded: number;
    edited: number;
    scheduled: number;
    published: number;
    archived: number;
  };
  by_client: Array<{
    client_id: string;
    client_name: string;
    count: number;
  }>;
  monthly_progress: Array<{
    month: number;
    year: number;
    count: number;
  }>;
}

export interface EventoCalendario {
  id: string;
  title: string;
  description?: string;
  event_type: 'recording' | 'deadline' | 'publishing' | 'meeting' | 'review';
  start_date: Date;
  end_date?: Date;
  all_day: boolean;
  timezone: string;
  client_id?: string;
  content_id?: string;
  created_by: string;
  assigned_to: string[];
  priority: 'low' | 'medium' | 'high' | 'urgent';
  status: 'scheduled' | 'in_progress' | 'completed' | 'cancelled';
  location?: string;
  meeting_link?: string;
  reminder_sent: boolean;
  reminder_minutes: number;
  created_at: Date;
  updated_at: Date;
}

export interface Action {
  type: 'SET_CLIENTI' | 'ADD_CLIENTE' | 'UPDATE_CLIENTE' | 'DELETE_CLIENTE' |
        'SET_CONTENUTI' | 'ADD_CONTENUTO' | 'UPDATE_CONTENUTO' | 'DELETE_CONTENUTO' |
        'SET_EVENTI' | 'SET_DASHBOARD_STATS' | 'SET_CLIENT_STATS' | 'SET_CONTENT_STATS';
  payload: Cliente[] | Cliente | Partial<Cliente> | string | Contenuto[] | Contenuto | Partial<Contenuto> | EventoCalendario[] | DashboardStats | ClientStats | ContentStats;
}

export * from './calendar'; 
export interface Cover {
  id: string;
  client_id: string;
  title: string;
  image_url: string;
  created_by: string;
  created_at: string;
}
